/**
 * The landing page's icon set, lifted from the wireframe's SVG sprite.
 *
 * The names match the backend's `Icon` literal - the admin picks one by name
 * for each highlight and "why us" card - plus the page's own UI icons.
 */

const STROKE = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.8,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
};

const ICONS = {
  bag: (
    <g {...STROKE}>
      <path d="M6 7h12l1 13H5L6 7Z" />
      <path d="M9 10V6a3 3 0 0 1 6 0v4" />
    </g>
  ),
  search: (
    <g {...STROKE}>
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-3.5-3.5" />
    </g>
  ),
  user: (
    <g {...STROKE}>
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21c1.5-4 4.5-6 8-6s6.5 2 8 6" />
    </g>
  ),
  heart: (
    <g {...STROKE}>
      <path d="M12 20s-7.5-4.6-9.2-9.3C1.6 7.4 3.8 4 7.2 4c2 0 3.4 1.1 4.8 2.8C13.4 5.1 14.8 4 16.8 4c3.4 0 5.6 3.4 4.4 6.7C19.5 15.4 12 20 12 20Z" />
    </g>
  ),
  cart: (
    <g {...STROKE}>
      <path d="M3 4h2l2.4 11.2a1.5 1.5 0 0 0 1.5 1.2h8.6a1.5 1.5 0 0 0 1.5-1.1L21 8H6.2" />
      <circle cx="9.5" cy="20" r="1.3" />
      <circle cx="17" cy="20" r="1.3" />
    </g>
  ),
  menu: (
    <g {...STROKE}>
      <path d="M4 7h16M4 12h16M4 17h10" />
    </g>
  ),
  close: (
    <g {...STROKE} strokeWidth={2}>
      <path d="M6 6l12 12M18 6 6 18" />
    </g>
  ),
  arrow: (
    <g {...STROKE} strokeWidth={2}>
      <path d="M5 12h14M13 6l6 6-6 6" />
    </g>
  ),
  chev: (
    <g {...STROKE} strokeWidth={2}>
      <path d="m9 6 6 6-6 6" />
    </g>
  ),
  truck: (
    <g {...STROKE}>
      <path d="M2 6h11v10H2zM13 10h4l4 3v3h-8" />
      <circle cx="6.5" cy="17.5" r="1.8" />
      <circle cx="17" cy="17.5" r="1.8" />
    </g>
  ),
  lock: (
    <g {...STROKE}>
      <rect x="4.5" y="10.5" width="15" height="10" rx="2.5" />
      <path d="M8 10.5V7.5a4 4 0 0 1 8 0v3" />
      <circle cx="12" cy="15.5" r="1.2" />
    </g>
  ),
  return: (
    <g {...STROKE}>
      <path d="M9 14 4 9l5-5" />
      <path d="M4 9h10.5a5.5 5.5 0 0 1 0 11H11" />
    </g>
  ),
  chat: (
    <g {...STROKE}>
      <path d="M4 5h16v11H9l-5 4V5Z" />
      <path d="M8.5 10.5h.01M12 10.5h.01M15.5 10.5h.01" strokeWidth={2.4} />
    </g>
  ),
  check: (
    <g {...STROKE} strokeWidth={2}>
      <circle cx="12" cy="12" r="9" />
      <path d="m8 12.5 2.7 2.7L16 10" />
    </g>
  ),
  shield: (
    <g {...STROKE} strokeWidth={2}>
      <path d="M12 3 4.5 6v5.5c0 4.5 3.2 8.2 7.5 9.5 4.3-1.3 7.5-5 7.5-9.5V6L12 3Z" />
      <path d="m9 12 2 2 4-4" />
    </g>
  ),
  mail: (
    <g {...STROKE}>
      <rect x="3" y="5" width="18" height="14" rx="2.5" />
      <path d="m4 7 8 6 8-6" />
    </g>
  ),
  star: (
    <path
      fill="currentColor"
      d="m12 3 2.7 5.6 6.1.9-4.4 4.3 1 6.1L12 17l-5.4 2.9 1-6.1-4.4-4.3 6.1-.9L12 3Z"
    />
  ),
  tag: (
    <g {...STROKE}>
      <path d="M3 12V4h8l10 10-8 8L3 12Z" />
      <circle cx="7.5" cy="8.5" r="1.4" />
    </g>
  ),
  instagram: (
    <g {...STROKE}>
      <rect x="3.5" y="3.5" width="17" height="17" rx="5" />
      <circle cx="12" cy="12" r="4" />
      <circle cx="17.2" cy="6.8" r="1" fill="currentColor" stroke="none" />
    </g>
  ),
  facebook: (
    <path
      fill="currentColor"
      d="M13.5 21v-7.5h2.6l.4-3h-3V8.6c0-.9.3-1.5 1.5-1.5h1.6V4.4c-.3 0-1.2-.1-2.3-.1-2.3 0-3.8 1.4-3.8 3.9v2.3H8v3h2.5V21h3Z"
    />
  ),
  x: (
    <path
      fill="currentColor"
      d="M17.5 3h3.1l-6.8 7.8 8 10.2h-6.3l-4.9-6.4L5 21H1.9l7.3-8.3L1.5 3h6.4l4.4 5.9L17.5 3Zm-1.1 16.2h1.7L7.1 4.7H5.3l11.1 14.5Z"
    />
  ),
  youtube: (
    <path
      fill="currentColor"
      d="M21.6 7.2a2.5 2.5 0 0 0-1.8-1.8C18.2 5 12 5 12 5s-6.2 0-7.8.4A2.5 2.5 0 0 0 2.4 7.2 26 26 0 0 0 2 12a26 26 0 0 0 .4 4.8 2.5 2.5 0 0 0 1.8 1.8C5.8 19 12 19 12 19s6.2 0 7.8-.4a2.5 2.5 0 0 0 1.8-1.8A26 26 0 0 0 22 12a26 26 0 0 0-.4-4.8ZM10 15V9l5.2 3L10 15Z"
    />
  ),
};

/** The icons an admin may choose for highlights and "why us" cards. */
export const CHOOSABLE_ICONS = [
  'truck',
  'return',
  'lock',
  'chat',
  'shield',
  'tag',
  'star',
  'bag',
  'heart',
  'mail',
];

export function SiteIcon({ name, className = 'i', ...props }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true" focusable="false" {...props}>
      {ICONS[name] ?? ICONS.tag}
    </svg>
  );
}
